import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, EnvelopeSimple, LockKey, Clipboard } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { Input } from "../components/ui/input";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const { login, user } = useAuth();

  useEffect(() => {
    if (user) navigate(user.rolle === "berater" ? "/berater" : "/app", { replace: true });
  }, [user, navigate]);

  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestCode = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/request-code", { email, ref_code: ref });
      setDevCode(data.dev_code || "");
      setStep("code");
      setCooldown(30);
      toast.success("Wir haben deinen Anmeldecode erstellt.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (cooldown > 0 || loading) return;
    setLoading(true);
    try {
      await api.post("/auth/request-code", { email, ref_code: ref });
      setCooldown(30);
      toast.success("Neuer Code wurde gesendet.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-code", { email, code });
      login(data.token, data.user);
      toast.success("Willkommen!");
      navigate(data.user.rolle === "berater" ? "/berater" : "/app");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const pasteCode = async () => {
    try {
      const t = await navigator.clipboard.readText();
      const digits = (t.match(/\d/g) || []).join("").slice(0, 6);
      if (!digits) return toast.error("Kein Code in der Zwischenablage gefunden.");
      setCode(digits);
      toast.success("Code eingefügt.");
    } catch {
      toast.error("Einfügen nicht möglich. Bitte Code manuell eintippen.");
    }
  };

  return (
    <div className="mx-auto min-h-screen max-w-md bg-[#F8F9FA]">
      <div className="flex items-center justify-between px-6 pb-2 pt-[calc(env(safe-area-inset-top)+1.5rem)]">
        <button
          data-testid="back-btn"
          onClick={() => (step === "code" ? setStep("email") : navigate("/"))}
          className="flex h-10 w-10 items-center justify-center rounded-full text-[#0A1128] transition-colors hover:bg-black/5"
        >
          <ArrowLeft size={22} />
        </button>
        <Logo />
        <div className="w-10" />
      </div>

      <motion.div
        key={step}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="px-7 pt-16"
      >
        {step === "email" ? (
          <>
            <div className="mb-4 h-px w-12 bg-[#C5A059]" />
            <h1 className="font-display text-4xl font-light tracking-tight text-[#0A1128]">Anmelden</h1>
            <p className="mt-3 text-base text-[#718096]">
              Gib deine E-Mail-Adresse ein. Wir speichern deinen Fortschritt sicher damit.
            </p>
            <form onSubmit={requestCode} className="mt-10 space-y-5">
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.1em] text-[#C5A059]">
                  E-Mail
                </label>
                <div className="relative">
                  <EnvelopeSimple size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A0AEC0]" />
                  <Input
                    data-testid="email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@beispiel.de"
                    className="h-14 rounded-xl border-[#E2E8F0] bg-white pl-12 text-base focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]"
                  />
                </div>
              </div>
              <button
                data-testid="request-code-btn"
                type="submit"
                disabled={loading}
                className="h-14 w-full rounded-full bg-[#0A1128] text-base font-semibold text-white transition-all duration-200 active:scale-[0.98] disabled:opacity-60"
              >
                {loading ? "Bitte warten…" : "Code anfordern"}
              </button>
              <button type="button" data-testid="to-register-link" onClick={() => navigate("/berater-werden")} className="mt-4 w-full text-center text-sm text-[#718096] transition-colors hover:text-[#0A1128]">
                Neu hier? Als Berater registrieren
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="mb-4 h-px w-12 bg-[#C5A059]" />
            <h1 className="font-display text-4xl font-light tracking-tight text-[#0A1128]">Code eingeben</h1>
            <p className="mt-3 text-base text-[#718096]">
              Gesendet an <span className="font-semibold text-[#0A1128]">{email}</span>.
            </p>

            <div className="mt-6 rounded-xl border border-[#C5A059]/40 bg-[#FBF8F1] p-4" data-testid="code-sent-box">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#C5A059]">Code gesendet</p>
              <p className="mt-1 text-sm text-[#718096]">Wir haben dir einen 6-stelligen Code per E-Mail geschickt. Bitte prüfe dein Postfach (ggf. auch Spam).</p>
            </div>

            <form onSubmit={verify} className="mt-8 space-y-5">
              <div className="relative">
                <LockKey size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A0AEC0]" />
                <Input
                  data-testid="code-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  name="one-time-code"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="6-stelliger Code"
                  className="h-14 rounded-xl border-[#E2E8F0] bg-white pl-12 text-lg tracking-[0.3em] focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]"
                />
              </div>
              <button
                type="button"
                data-testid="paste-code-btn"
                onClick={pasteCode}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-full border border-[#0A1128]/15 bg-white text-sm font-semibold text-[#0A1128] transition-colors hover:bg-black/5 active:scale-[0.99]"
              >
                <Clipboard size={17} /> Aus Zwischenablage einfügen
              </button>
              <button
                data-testid="verify-code-btn"
                type="submit"
                disabled={loading || code.length < 6}
                className="h-14 w-full rounded-full bg-[#C5A059] text-base font-semibold text-[#0A1128] shadow-[0_8px_30px_rgba(197,160,89,0.3)] transition-all duration-200 active:scale-[0.98] disabled:opacity-60"
              >
                {loading ? "Prüfe…" : "Anmelden"}
              </button>
              <button
                type="button"
                data-testid="resend-code-btn"
                onClick={resendCode}
                disabled={cooldown > 0 || loading}
                className="w-full text-center text-sm text-[#718096] transition-colors hover:text-[#0A1128] disabled:opacity-50"
              >
                {cooldown > 0 ? `Code erneut senden (${cooldown}s)` : "Code erneut senden"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
